<template>
  <div class="tax-history-v2-detail">
    <v-container fluid>
      <v-toolbar color="primary" class="header-title text-white">
        <v-btn
          icon="mdi-arrow-left"
          variant="text"
          :aria-label="$t('taxHistoryV2Back')"
          data-testid="tax-history-v2-detail-back"
          @click="goBack"
        />
        <v-toolbar-title class="title">{{ $t('taxHistoryV2DetailTitle') }}</v-toolbar-title>
        <v-spacer />
        <div class="d-flex pr-4">
          <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
        </div>
      </v-toolbar>

      <v-skeleton-loader
        v-if="state.detailLoading"
        class="mt-3"
        type="article, table"
        data-testid="tax-history-v2-detail-loading"
      />

      <v-alert
        v-else-if="state.detailError"
        type="error"
        variant="tonal"
        class="mt-3"
        data-testid="tax-history-v2-detail-error"
      >
        <div class="d-flex align-center ga-2">
          <span>
            {{ state.detailErrorStatus === 404 ? $t('taxHistoryV2NotFound') : state.detailError }}
          </span>
          <v-spacer />
          <v-btn size="small" variant="outlined" data-testid="tax-history-v2-detail-retry" @click="reload">
            {{ $t('taxHistoryV2Retry') }}
          </v-btn>
          <v-btn size="small" variant="text" data-testid="tax-history-v2-detail-error-back" @click="goBack">
            {{ $t('taxHistoryV2Back') }}
          </v-btn>
        </div>
      </v-alert>

      <template v-else-if="form">
        <v-card class="card2 mt-3 record-header" data-testid="tax-history-v2-detail-header">
          <v-card-text>
            <div class="d-flex align-start justify-space-between flex-wrap ga-3">
              <div>
                <h1 class="record-title">{{ $t('taxHistoryV2TaxFormTitle', { id: form.formID }) }}</h1>
                <div class="text-caption text-medium-emphasis mt-1">
                  {{ $t('taxHistoryV2Cif') }} <CopyId :value="form.userID" :label="$t('taxHistoryV2Cif')" truncate />
                </div>
              </div>
              <div class="d-flex align-center flex-wrap ga-2">
                <v-chip :color="statusColor(form.status)" variant="tonal" label data-testid="tax-history-v2-detail-status">
                  {{ statusLabel(form.status) }}
                </v-chip>
                <v-chip
                  v-if="form.postCompleteEditStatus"
                  :color="postCompleteEditColor(form.postCompleteEditStatus)"
                  variant="outlined"
                  label
                  data-testid="tax-history-v2-detail-post-complete-edit"
                >
                  {{ postCompleteEditLabel(form.postCompleteEditStatus) }}
                </v-chip>
              </div>
            </div>

            <div class="d-flex align-center flex-wrap ga-2 mt-4">
              <v-chip size="small" variant="outlined" prepend-icon="mdi-calendar-outline">
                {{ form.taxYearCode || (form.yearID ? $t('taxHistoryV2Year', { id: form.yearID }) : $t('taxHistoryV2TaxYearNotSelected')) }}
              </v-chip>
              <v-chip size="small" variant="outlined" :color="form.isPlayground ? 'warning' : 'primary'" label>
                <v-icon start>{{ form.isPlayground ? 'mdi-flask-outline' : 'mdi-chat-outline' }}</v-icon>
                {{ form.isPlayground ? $t('taxHistoryV2Playground') : $t('taxHistoryV2Production') }}
              </v-chip>
              <v-chip size="small" variant="outlined">{{ $t('taxHistoryV2Created') }} {{ formatDate(form.createdDate) }}</v-chip>
              <v-chip size="small" variant="outlined">{{ $t('taxHistoryV2Updated') }} {{ formatDate(form.updatedDate) }}</v-chip>
              <v-chip v-if="form.completedDate" size="small" variant="outlined">
                {{ $t('taxHistoryV2CompletedAt') }} {{ formatDate(form.completedDate) }}
              </v-chip>
              <v-spacer />
              <v-btn
                v-if="form.sessionID"
                size="small"
                variant="outlined"
                prepend-icon="mdi-history"
                data-testid="tax-history-v2-open-tax-log"
                @click="openTaxLog"
              >
                {{ $t('taxHistoryV2OpenTaxLog') }}
              </v-btn>
              <!-- #776 — tooltip on a wrapper: a disabled button fires no hover events. -->
              <span class="d-inline-flex">
                <v-btn
                  color="success"
                  variant="flat"
                  size="small"
                  prepend-icon="mdi-file-delimited-outline"
                  :loading="exportLoading"
                  :disabled="exportLoading || answers.length === 0"
                  data-testid="tax-history-v2-export-csv"
                  @click="exportCsv"
                >
                  {{ $t('taxHistoryV2ExportCsv') }}
                </v-btn>
                <v-tooltip activator="parent" location="top">{{ $t('taxHistoryV2ExportCsvHint') }}</v-tooltip>
              </span>
            </div>

            <v-alert
              v-if="exportError"
              type="error"
              variant="tonal"
              density="compact"
              closable
              class="mt-3"
              data-testid="tax-history-v2-export-csv-error"
              @click:close="exportError = ''"
            >
              {{ exportError }}
            </v-alert>

            <div class="progress-block">
              <div class="d-flex justify-space-between text-caption text-medium-emphasis mb-1">
                <span>{{ $t('taxHistoryV2RequiredProgress') }}</span>
                <strong>{{ form.answeredCount || 0 }} / {{ form.totalRequired || 0 }}</strong>
              </div>
              <v-progress-linear
                :model-value="progressPct"
                :color="progressColor(form.status)"
                height="8"
                rounded
                :aria-label="$t('taxHistoryV2RequiredProgress')"
              />
              <div class="d-flex flex-wrap ga-2 mt-2">
                <v-chip size="x-small" variant="tonal" color="success">{{ $t('taxHistoryV2Resolved') }} {{ form.answeredCount || 0 }}</v-chip>
                <v-chip size="x-small" variant="tonal" color="warning">{{ $t('taxHistoryV2Partial') }} {{ form.partialCount || 0 }}</v-chip>
                <v-chip size="x-small" variant="tonal" color="grey">{{ $t('taxHistoryV2Skipped') }} {{ form.skippedCount || 0 }}</v-chip>
                <v-chip size="x-small" variant="tonal" color="purple">{{ $t('taxHistoryV2NotApplicable') }} {{ form.notApplicableCount || 0 }}</v-chip>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="card2 mt-3 answers-card" data-testid="tax-history-v2-answers-card">
          <v-card-title class="d-flex align-center flex-wrap ga-2">
            <v-icon>mdi-format-list-bulleted</v-icon>
            {{ $t('taxHistoryV2CapturedAnswers') }}
            <v-chip size="small" variant="tonal">{{ filteredAnswerCount }} / {{ answers.length }}</v-chip>
          </v-card-title>
          <v-card-text>
            <v-text-field
              v-model="answerSearch"
              prepend-inner-icon="mdi-magnify"
              :label="$t('taxHistoryV2AnswerSearch')"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              class="answer-search"
              data-testid="tax-history-v2-answer-search"
            />

            <div v-if="!filteredGroups.length" class="empty-state" data-testid="tax-history-v2-answers-empty">
              {{ $t('taxHistoryV2NoAnswerMatch') }}
            </div>

            <div v-else class="answers-scroll">
              <table class="answers-table">
                <thead>
                  <tr>
                    <th>{{ $t('taxHistoryV2Question') }}</th>
                    <th>{{ $t('taxHistoryV2RuleField') }}</th>
                    <th>{{ $t('taxHistoryV2Kind') }}</th>
                    <th>{{ $t('taxHistoryV2Status') }}</th>
                    <th>{{ $t('taxHistoryV2Answer') }}</th>
                    <th>{{ $t('taxHistoryV2AnswerSession') }}</th>
                    <th>{{ $t('taxHistoryV2Updated') }}</th>
                    <th>{{ $t('taxHistoryV2Technical') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="group in filteredGroups" :key="group.code">
                    <tr class="group-row">
                      <td colspan="8">
                        <v-icon size="16" class="mr-1">mdi-tag-outline</v-icon>
                        {{ group.label }}
                        <span class="text-caption text-medium-emphasis ml-1">({{ group.items.length }})</span>
                      </td>
                    </tr>
                    <tr v-for="answer in group.items" :key="answer.answerID" class="answer-row">
                      <td class="question-cell">{{ answer.questionTextTh || answer.fieldKey || '—' }}</td>
                      <td class="mono text-no-wrap">{{ answer.fieldKey || '—' }}</td>
                      <td class="text-no-wrap">{{ answer.itemKind || '—' }}</td>
                      <td>
                        <v-chip :color="statusColor(answer.answerStatus)" size="x-small" variant="tonal" label>
                          {{ answerStatusLabel(answer.answerStatus) }}
                        </v-chip>
                      </td>
                      <td class="answer-cell">{{ answerText(answer) }}</td>
                      <!-- Three states, and the middle one is the point: a blank session does NOT mean "unknown",
                           it means no conversation produced this row (seed placeholder / profile pre-fill). -->
                      <td class="mono text-no-wrap">
                        <span v-if="answer.sessionID">{{ answer.sessionID }}</span>
                        <span v-else-if="answer.isLlmParsed === false" class="text-medium-emphasis">
                          {{ $t('taxHistoryV2FromProfile') }}
                        </span>
                        <span v-else class="text-medium-emphasis">—</span>
                      </td>
                      <td class="text-no-wrap">{{ formatDate(answer.updatedDate || answer.createdDate) }}</td>
                      <td>
                        <v-btn
                          size="x-small"
                          variant="text"
                          :prepend-icon="technicalAnswerId === answer.answerID ? 'mdi-chevron-up' : 'mdi-code-json'"
                          :data-testid="`tax-history-v2-technical-${answer.answerID}`"
                          @click="toggleTechnical(answer)"
                        >
                          {{ $t('taxHistoryV2Details') }}
                        </v-btn>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>

            <v-expand-transition>
              <v-card v-if="technicalAnswer" variant="tonal" class="technical-answer mt-3" data-testid="tax-history-v2-technical-panel">
                <v-card-title class="text-subtitle-2">{{ $t('taxHistoryV2AnswerTechnical') }}</v-card-title>
                <v-card-text>
                  <pre class="code-block">{{ formatTechnical(technicalAnswer) }}</pre>
                </v-card-text>
              </v-card>
            </v-expand-transition>
          </v-card-text>
        </v-card>

        <v-expansion-panels class="mt-3" data-testid="tax-history-v2-form-technical">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <v-icon start size="18">mdi-wrench-outline</v-icon>
              {{ $t('taxHistoryV2FormTechnical') }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <dl class="technical-grid">
                <div><dt>{{ $t('taxHistoryV2FormId') }}</dt><dd class="mono">{{ form.formID }}</dd></div>
                <div><dt>{{ $t('taxHistoryV2Session') }}</dt><dd><CopyId :value="form.sessionID" :label="$t('taxHistoryV2Session')" truncate /></dd></div>
                <div><dt>{{ $t('taxHistoryV2Channel') }}</dt><dd>{{ form.channel || '—' }}</dd></div>
                <div><dt>{{ $t('taxHistoryV2CurrentGroup') }}</dt><dd class="mono">{{ form.currentGroupCode || '—' }}</dd></div>
                <div><dt>{{ $t('taxHistoryV2CompanyId') }}</dt><dd class="mono">{{ form.companyID }}</dd></div>
                <div><dt>{{ $t('taxHistoryV2ConfigVersion') }}</dt><dd class="mono">{{ form.taxConfigVersionID ?? '—' }}</dd></div>
                <div><dt>{{ $t('taxHistoryV2QuestionVersion') }}</dt><dd class="mono">{{ form.questionVersionID ?? '—' }}</dd></div>
              </dl>
              <div class="text-subtitle-2 mt-4 mb-2">{{ $t('taxHistoryV2ConfigFormProjection') }}</div>
              <pre class="code-block">{{ configFormJson }}</pre>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </v-container>
  </div>
</template>

<script>
import { state, loadFormDetail, formatDate } from '../utils/taxHistoryV2Store.js';
import CopyId from '../components/CopyId.vue';
import { taxHistoryV2Api } from '../services/taxHistoryV2Api.js';
import { exportTimestamp } from '../utils/taxV2ExcelExport.js';

export default {
  name: 'TaxHistoryV2Detail',
  components: { CopyId },
  data() {
    return {
      answerSearch: '',
      technicalAnswerId: null,
      exportLoading: false,
      exportError: '',
    };
  },
  computed: {
    today() {
      const locale = this.$i18n?.locale === 'th' ? 'th-TH' : 'en-US';
      return new Date().toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    },
    state() {
      return state;
    },
    form() {
      return state.detail?.form || null;
    },
    answers() {
      return state.detail?.answers || [];
    },
    technicalAnswer() {
      return this.answers.find((answer) => answer.answerID === this.technicalAnswerId) || null;
    },
    filteredGroups() {
      const query = (this.answerSearch || '').trim().toLowerCase();
      const groups = new Map();
      for (const answer of this.answers) {
        const searchable = [
          answer.questionTextTh,
          answer.fieldKey,
          answer.itemKind,
          answer.answerStatus,
          answer.rawAnswer,
          answer.normalizedValue,
          answer.reason,
          answer.groupCode,
        ].filter(Boolean).join(' ').toLowerCase();
        if (query && !searchable.includes(query)) continue;

        const code = answer.groupCode || 'UNGROUPED';
        if (!groups.has(code)) {
          groups.set(code, { code, label: this.groupLabel(code), items: [] });
        }
        groups.get(code).items.push(answer);
      }
      return Array.from(groups.values());
    },
    filteredAnswerCount() {
      return this.filteredGroups.reduce((count, group) => count + group.items.length, 0);
    },
    progressPct() {
      if (!this.form || !this.form.totalRequired) return 0;
      return Math.min(100, Math.round((this.form.answeredCount / this.form.totalRequired) * 100));
    },
    configFormJson() {
      return this.prettyJson(state.detail?.configForm);
    },
  },
  async created() {
    await this.reload();
  },
  methods: {
    formatDate,
    async reload() {
      try {
        await loadFormDetail(this.$route.params.formId);
      } catch (error) {
        // The store owns the error state used by the template.
      }
    },
    goBack() {
      this.$router.push({ name: 'TaxHistoryV2' });
    },
    openTaxLog() {
      if (!this.form?.sessionID) return;
      this.$router.push({ name: 'TaxLogV2Detail', params: { sessionId: this.form.sessionID } });
    },
    groupLabel(code) {
      const text = String(code || '').replace(/[_-]+/g, ' ').trim();
      return text ? text.replace(/\b\w/g, (character) => character.toUpperCase()) : this.$t('taxHistoryV2Ungrouped');
    },
    statusLabel(status) {
      const labels = {
        inprogress: 'taxHistoryV2InProgress',
        completed: 'taxHistoryV2Completed',
        abandoned: 'taxHistoryV2Abandoned',
      };
      const key = String(status || '').toLowerCase();
      return status ? this.$t(labels[key] || status) : '—';
    },
    // Second header chip: a Completed plan the customer re-opened with «แก้ไขแผน» (Status stays Completed).
    postCompleteEditLabel(value) {
      const labels = {
        editing: 'taxHistoryV2EditingAfterComplete',
        edited: 'taxHistoryV2EditedAfterComplete',
      };
      const key = String(value || '').toLowerCase();
      return labels[key] ? this.$t(labels[key]) : (value || '');
    },
    postCompleteEditColor(value) {
      switch (String(value || '').toLowerCase()) {
        case 'editing': return 'info';
        case 'edited': return 'grey';
        default: return 'grey';
      }
    },
    answerStatusLabel(status) {
      const labels = {
        answered: 'taxHistoryV2Answered',
        partialanswer: 'taxHistoryV2Partial',
        partial_answer: 'taxHistoryV2Partial',
        skipped: 'taxHistoryV2Skipped',
        notapplicable: 'taxHistoryV2NotApplicable',
        not_applicable: 'taxHistoryV2NotApplicable',
        notstart: 'taxHistoryV2NotStarted',
        not_start: 'taxHistoryV2NotStarted',
        pending: 'taxHistoryV2Pending',
      };
      const key = String(status || '').toLowerCase();
      return status ? this.$t(labels[key] || status) : this.$t('taxHistoryV2NotStarted');
    },
    statusColor(status) {
      switch (String(status || '').toLowerCase()) {
        case 'answered': return 'success';
        case 'partialanswer': return 'warning';
        case 'skipped': return 'grey';
        case 'notapplicable': return 'purple';
        case 'pending': return 'primary';
        case 'completed': return 'success';
        case 'inprogress': return 'warning';
        default: return 'grey';
      }
    },
    progressColor(status) {
      if (status === 'Completed') return 'success';
      if (status === 'Abandoned') return 'grey';
      return 'primary';
    },
    answerText(answer) {
      if (!answer) return '—';
      if (answer.rawAnswer) return answer.rawAnswer;
      if (answer.normalizedValue) return answer.normalizedValue;
      const values = this.parseJson(answer.answerValuesJson);
      if (Array.isArray(values) && values.length) return values.join(', ');
      if (values && typeof values === 'object') return this.$t('taxHistoryV2StructuredValues');
      if (answer.answerStatus === 'NotApplicable') return this.$t('taxHistoryV2NotApplicable');
      if (answer.answerStatus === 'Skipped') return this.$t('taxHistoryV2Skipped');
      return '—';
    },
    /** #776 — every answer of this form as the API's CSV: the same rows the list page's ZIP holds for it (as .xlsx). */
    async exportCsv() {
      if (!this.form || this.exportLoading) return;
      this.exportLoading = true;
      this.exportError = '';
      try {
        const blob = await taxHistoryV2Api.exportFormCsv(this.form.formID, this.$i18n?.locale);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        try {
          link.href = url;
          link.download = `Tax_Form_${this.form.formID}_${exportTimestamp()}.csv`;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
        } finally {
          link.remove();
          window.URL.revokeObjectURL(url);
        }
      } catch (error) {
        this.exportError = this.$t('taxHistoryV2ExportCsvFailed', {
          message: error?.httpStatus ? `HTTP ${error.httpStatus}` : (error?.message || 'Request failed'),
        });
      } finally {
        this.exportLoading = false;
      }
    },
    toggleTechnical(answer) {
      this.technicalAnswerId = this.technicalAnswerId === answer.answerID ? null : answer.answerID;
    },
    formatTechnical(answer) {
      return this.prettyJson({
        answerStatus: answer.answerStatus,
        isComplete: answer.isComplete,
        reason: answer.reason,
        rawAnswer: answer.rawAnswer,
        normalizedValue: answer.normalizedValue,
        answerValues: this.parseJson(answer.answerValuesJson),
        subItems: this.parseJson(answer.subItemsJson),
        meta: this.parseJson(answer.metaJson),
        isLlmParsed: answer.isLlmParsed,
      });
    },
    parseJson(value) {
      if (!value) return null;
      if (typeof value !== 'string') return value;
      try {
        return JSON.parse(value);
      } catch (error) {
        return value;
      }
    },
    prettyJson(value) {
      if (value == null) return '—';
      if (typeof value === 'string') {
        const parsed = this.parseJson(value);
        if (parsed !== value) return JSON.stringify(parsed, null, 2);
        return value;
      }
      return JSON.stringify(value, null, 2);
    },
  },
};
</script>

<style scoped>
.record-title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 600;
}

.progress-block {
  max-width: 720px;
  margin-top: 22px;
}

.answer-search {
  max-width: 520px;
  margin-bottom: 14px;
}

.answers-scroll {
  max-height: 66vh;
  overflow: auto;
}

.answers-table {
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.answers-table th,
.answers-table td {
  padding: 8px 10px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.1);
  text-align: left;
  vertical-align: top;
}

.answers-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
  font-weight: 600;
  white-space: nowrap;
}

.group-row td {
  background: rgba(var(--v-theme-primary), 0.07);
  border-top: 1px solid rgba(var(--v-theme-primary), 0.2);
  font-weight: 600;
}

.question-cell {
  min-width: 260px;
  max-width: 440px;
}

.answer-cell {
  min-width: 180px;
  max-width: 360px;
  overflow-wrap: anywhere;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  overflow-wrap: anywhere;
}

.technical-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px 24px;
  margin: 0;
}

.technical-grid dt {
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 0.75rem;
}

.technical-grid dd {
  margin: 2px 0 0;
  font-size: 0.875rem;
  font-weight: 500;
}

.code-block {
  max-height: 360px;
  margin: 0;
  overflow: auto;
  padding: 12px;
  border-radius: 6px;
  background: #0f1115;
  color: #dbe2ea;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.75rem;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.empty-state {
  padding: 32px 16px;
  color: rgba(var(--v-theme-on-surface), 0.62);
  text-align: center;
}

@media (prefers-reduced-motion: reduce) {
  .tax-history-v2-detail * {
    transition-duration: 0ms !important;
  }
}
</style>
